// src/assets/js/quizzes/ui/QuizzesView.js
// UI pura: render + listeners. No fetch, no state, no reglas.

export class QuizzesView {
	/**
	 * @param {object} params
	 * @param {HTMLElement} params.root
	 * @param {object} [params.selectors]
	 */
	constructor({ root, selectors = {} } = {}) {
		if (!root) throw new Error('[QuizzesView] root is required');

		this.root = root;

		this.sel = {
			subjectsGrid: selectors.subjectsGrid || '[data-quizzes-subjects-grid]',
			list: selectors.list || '[data-quizzes-list]',
			listItems: selectors.listItems || '[data-quizzes-list-items]',
			searchInput: selectors.searchInput || '[data-quizzes-search-input]',
			subjectSelect: selectors.subjectSelect || '[data-quizzes-subject-select]',
			regionSelect: selectors.regionSelect || '[data-quizzes-region-select]',
			clearBtn: selectors.clearBtn || '[data-quizzes-clear]',
			loadMoreBtn: selectors.loadMoreBtn || '[data-quizzes-load-more]',
			viewToggleSubjects: selectors.viewToggleSubjects || '[data-quizzes-view="subjects"]',
			viewToggleRegions: selectors.viewToggleRegions || '[data-quizzes-view="regions"]',
			regionsGrid: selectors.regionsGrid || '[data-quizzes-regions-grid]',
			status: selectors.status || '[data-quizzes-status]',
			pager: selectors.pager || '[data-quizzes-pager]',
			loadedCount: selectors.loadedCount || '[data-quizzes-loaded]',
			totalCount: selectors.totalCount || '[data-quizzes-total]',
			resultsCount: selectors.resultsCount || '[data-quizzes-count]',
			...selectors,
		};

		this.tpl = {
			subjectCard: this._getTemplate('#tpl-subject-card'),
			quizItem: this._getTemplate('#tpl-quiz-item'),
			regionCard: this._getTemplate('#tpl-region-card'),
		};

		this.els = {
			subjectsGrid: this.root.querySelector(this.sel.subjectsGrid),
			list: this.root.querySelector(this.sel.list),
			listItems: this.root.querySelector(this.sel.listItems),
			searchInput: this.root.querySelector(this.sel.searchInput),
			subjectSelect: this.root.querySelector(this.sel.subjectSelect),
			regionSelect: this.root.querySelector(this.sel.regionSelect),
			clearBtn: this.root.querySelector(this.sel.clearBtn),
			loadMoreBtn: this.root.querySelector(this.sel.loadMoreBtn),
			viewToggleSubjects: this.root.querySelector(this.sel.viewToggleSubjects),
			viewToggleRegions: this.root.querySelector(this.sel.viewToggleRegions),
			regionsGrid: this.root.querySelector(this.sel.regionsGrid),
			status: this.root.querySelector(this.sel.status),
			pager: this.root.querySelector(this.sel.pager),
			loadedCount: this.root.querySelector(this.sel.loadedCount),
			totalCount: this.root.querySelector(this.sel.totalCount),
			resultsCount: this.root.querySelector(this.sel.resultsCount),
		};

		this._handlers = {};
	}

	/* =========================
	   Public events (Controller hooks)
	========================= */

	on(eventName, handler) {
		this._handlers[eventName] = handler;
	}

	bindUI() {
		// Search
		if (this.els.searchInput) {
			this.els.searchInput.addEventListener('input', (e) => {
				this._emit('search', { query: e.target.value });
			});
		}

		// Subject select
		if (this.els.subjectSelect) {
			this.els.subjectSelect.addEventListener('change', (e) => {
				const subjectId = e.target.value || null;
				console.log('[QuizzesView] emit subject', subjectId);
				this._emit('subject', { subjectId });
			});
		}

		// Region select (optional)
		if (this.els.regionSelect) {
			this.els.regionSelect.addEventListener('change', (e) => {
				const value = e.target.value || null;
				console.log('[QuizzesView] emit region', regionId);
				this._emit('region', { regionId: value });
			});
		}

		// Clear filters
		if (this.els.clearBtn) {
			this.els.clearBtn.addEventListener('click', () => {
				this._emit('clear');
			});
		}

		// Load more
		if (this.els.loadMoreBtn) {
			this.els.loadMoreBtn.addEventListener('click', () => {
				this._emit('loadMore');
			});
		}

		// View toggles
		if (this.els.viewToggleSubjects) {
			this.els.viewToggleSubjects.addEventListener('click', () => {
				this._emit('view', { viewMode: 'subjects' });
			});
		}
		if (this.els.viewToggleRegions) {
			this.els.viewToggleRegions.addEventListener('click', () => {
				this._emit('view', { viewMode: 'regions' });
			});
		}

		if (this.els.subjectsGrid) {
			this.els.subjectsGrid.addEventListener('click', (e) => {
				const btn = e.target.closest('[data-action]');
				if (!btn) return;

				const action = btn.dataset.action;

				if (action === 'open-subject') {
					const subjectId = String(btn.dataset.subjectId || '').trim();
					if (!subjectId) return;
					e.preventDefault();
					e.stopPropagation();
					this._emit('openSubject', { subjectId });
					return;
				}

				if (action === 'select-subject') {
					this._emit('subject', { subjectId: btn.dataset.subjectId || null });
				}
			});
		}

		// Delegación: click en cards de regiones
		if (this.els.regionsGrid) {
			this.els.regionsGrid.addEventListener('click', (e) => {
				const btn = e.target.closest('[data-action="select-region"]');
				if (!btn) return;
				this._emit('region', { regionId: btn.dataset.regionId || null });
			});
		}

		// Delegación: items de quiz (por si añades acciones)
		if (this.els.listItems) {
			this.els.listItems.addEventListener('click', (e) => {
				const openBtn = e.target.closest('[data-action="open-quiz"]');
				if (!openBtn) return;
				this._emit('openQuiz', {
					subjectId: openBtn.dataset.subjectId || null,
					quizId: openBtn.dataset.quizId || null,
				});
			});
		}
	}

	/* =========================
	   Render API (Controller calls)
	========================= */

	setStatus(text) {
		if (!this.els.status) return;
		this.els.status.textContent = text || '';
		this.els.status.style.display = text ? '' : 'none';
	}

	setActiveView(mode) {
		const isSubjects = mode === 'subjects';
		const isList = mode === 'list';

		// Subjects grid
		if (this.els.subjectsGrid) {
			this.els.subjectsGrid.toggleAttribute('hidden', !isSubjects);
		}

		// Quizzes list container (wrapper)
		if (this.els.list) {
			this.els.list.toggleAttribute('hidden', !isList);
		}

		// Pager: solo en list
		if (this.els.pager) {
			this.els.pager.toggleAttribute('hidden', !isList);
		}
	}

	renderSubjectOptions(subjects = []) {
		if (!this.els.subjectSelect) return;

		// Mantén "Todas" y rellena el resto
		this.els.subjectSelect.innerHTML = '<option value="">Todas</option>';

		for (const s of subjects) {
			const opt = document.createElement('option');
			opt.value = String(s.subjectId || '');
			opt.textContent = String(s.subjectTitle || s.subjectId || '');
			this.els.subjectSelect.appendChild(opt);
		}
	}

	render(vm) {
		this.setActiveView(vm.viewMode);

		this.setInputs(vm.inputs);
		this.setPagerStatus(vm.pager);
		this.setPagerVisibility(vm);

		if (vm.viewMode === 'subjects') {
			this.renderSubjects(vm.subjectCards);
		} else {
			this.renderQuizList(vm.quizItems);
		}
	}

	renderSubjects(subjectCards = []) {
		if (!this.els.subjectsGrid) return;

		this.els.subjectsGrid.innerHTML = '';
		const frag = document.createDocumentFragment();

		for (const card of subjectCards) {
			const node = this._clone(this.tpl.subjectCard);

			// -----------------------------
			// Datos base
			// card: { subjectId, subjectTitle, total, latest: [{ quizId, quizTitle, region, regionId }] }
			// -----------------------------
			const subjectId = String(card.subjectId || '');
			const subjectTitle = String(card.subjectTitle || 'Materia');
			const total = Number.isFinite(card.total) ? card.total : Number(card.total ?? 0);

			const article = node.querySelector('.subject-card');
			if (article) article.dataset.subject = subjectId;

			// Nombre materia
			const nameEl = node.querySelector('.subject-card__name');
			if (nameEl) nameEl.textContent = subjectTitle;

			// Icono materia (mismo patrón que index.html)
			const iconEl = node.querySelector('.subject-card__icon');
			if (iconEl) iconEl.setAttribute('data-icon', `subject-${subjectId}-icon`);

			// Count
			const countStrong = node.querySelector('.subject-card__count strong');
			if (countStrong) countStrong.textContent = String(total);

			// Botón "ver materia"
			const openBtn = node.querySelector(
				'[data-action="open-subject"], [data-action="select-subject"], [data-quizzes-open-subject]',
			);
			if (openBtn) {
				openBtn.dataset.subjectId = subjectId;
				openBtn.dataset.subjectTitle = subjectTitle;
			}

			// -----------------------------
			// Últimos 2–3 quizzes
			// -----------------------------
			const list = node.querySelector('.subject-card__list');
			if (list) {
				list.innerHTML = '';

				(card.latest || []).slice(0, 3).forEach((q) => {
					const li = document.createElement('li');

					const quizTitle = String(q.quizTitle || q.title || 'Quiz');
					const quizId = String(q.quizId || q.id || '');
					const regionName = q.region ? String(q.region) : null;
					const regionId = q.regionId ? String(q.regionId) : null;

					// Título (si quieres mostrar región, lo metemos aquí en pequeño)
					const titleSpan = document.createElement('span');
					titleSpan.className = 'subject-card__quiz-title';
					titleSpan.textContent = regionName ? `${quizTitle} · [${regionId}]` : quizTitle;

					// ⭐ Botón favorito (nuevo sistema multicolor)
					const favBtn = document.createElement('button');
					favBtn.className = 'fav-btn';
					favBtn.type = 'button';
					favBtn.setAttribute('data-action', 'fav-quiz');
					favBtn.setAttribute('aria-label', 'Marcar como favorito');
					favBtn.setAttribute('aria-pressed', 'false');

					// dataset necesario para favorites.js
					favBtn.dataset.quizId = quizId;
					favBtn.dataset.quizTitle = quizTitle;
					favBtn.dataset.subjectId = subjectId;
					favBtn.dataset.subjectTitle = subjectTitle;
					if (regionName) favBtn.dataset.region = regionName;
					if (regionId) favBtn.dataset.regionId = regionId;

					favBtn.disabled = !quizId;

					// Icono (multicolor vía CSS)
					const icon = document.createElement('span');
					icon.className = 'fav-icon fav-icon--mini';
					icon.setAttribute('aria-hidden', 'true');

					favBtn.appendChild(icon);

					li.appendChild(titleSpan);
					li.appendChild(document.createTextNode(' '));
					li.appendChild(favBtn);

					list.appendChild(li);
				});
			}

			frag.appendChild(node);
		}

		this.els.subjectsGrid.appendChild(frag);
	}

	renderRegions(regionCards = []) {
		if (!this.els.regionsGrid) return;
		this.els.regionsGrid.innerHTML = '';

		const frag = document.createDocumentFragment();
		for (const card of regionCards) {
			const node = this._clone(this.tpl.regionCard);
			// card: { regionId, regionName, total }
			node
				.querySelector('[data-bind="regionName"]')
				?.replaceChildren(document.createTextNode(card.regionName || 'Región'));
			node.querySelector('[data-bind="count"]')?.replaceChildren(document.createTextNode(String(card.total ?? 0)));

			const btn = node.querySelector('[data-action="select-region"]');
			if (btn) btn.dataset.regionId = card.regionId;

			frag.appendChild(node);
		}
		this.els.regionsGrid.appendChild(frag);
	}

	renderQuizList(items = []) {
		if (!this.els.listItems) return;
		this.els.listItems.innerHTML = '';

		const frag = document.createDocumentFragment();

		//items = Array(100).fill(items).flat();

		for (const item of items) {
			const node = this._clone(this.tpl.quizItem);

			// item: { quizId, quizTitle, subjectId, subjectTitle, region, regionId }
			const quizId = String(item.quizId || '');
			const quizTitle = String(item.quizTitle || 'Quiz');
			const subjectId = String(item.subjectId || '');
			const subjectTitle = String(item.subjectTitle || '');
			const region = String(item.region || '');
			const regionId = String(item.regionId || '');
			const meta = String(item.meta || item.exam || item.call || '');

			node.querySelector('[data-bind="quizTitle"]')?.replaceChildren(document.createTextNode(item.quizTitle || 'Quiz'));

			node
				.querySelector('[data-bind="subjectTitle"]')
				?.replaceChildren(document.createTextNode(item.subjectTitle || ''));

			node.querySelector('[data-bind="region"]')?.replaceChildren(document.createTextNode(item.region || ''));

			// meta pill (opcional)
			const metaEl = node.querySelector('[data-bind="meta"]');
			if (metaEl) {
				if (meta) {
					metaEl.replaceChildren(document.createTextNode(meta));
					metaEl.toggleAttribute('hidden', false);
				} else {
					metaEl.replaceChildren(document.createTextNode(''));
					metaEl.toggleAttribute('hidden', true);
				}
			}

			// ✅ Icono de materia (reusa subject-icons.scss)
			const iconEl = node.querySelector('[data-bind="subjectIcon"]');
			if (iconEl) {
				iconEl.classList.add('subject-icon'); // asegura ::before
				// Ej: subject-matematicas_ii-icon
				iconEl.dataset.icon = item.subjectId ? `subject-${item.subjectId}-icon` : '';
				iconEl.toggleAttribute('hidden', !item.subjectId);
			}

			// Botón abrir quiz
			const openBtn = node.querySelector('[data-action="open-quiz"]');
			if (openBtn) {
				openBtn.dataset.quizId = quizId;
				openBtn.dataset.subjectId = subjectId;
				openBtn.dataset.quizTitle = quizTitle;
				openBtn.dataset.subjectTitle = subjectTitle;
				if (region) openBtn.dataset.region = region;
				if (regionId) openBtn.dataset.regionId = regionId;
			}

			// ⭐ Botón favorito (ESTO ES LO QUE FALTABA)
			const favBtn = node.querySelector('[data-action="fav-quiz"]');
			if (favBtn) {
				favBtn.dataset.quizId = quizId || '';
				favBtn.dataset.quizTitle = quizTitle || '';
				favBtn.dataset.subjectId = subjectId || '';
				favBtn.dataset.subjectTitle = subjectTitle || '';
				if (region) favBtn.dataset.region = region;
				if (regionId) favBtn.dataset.regionId = regionId;
				favBtn.disabled = !quizId; // si no hay quizId, se deshabilita
			}

			frag.appendChild(node);
		}

		this.els.listItems.appendChild(frag);
	}

	setPagerVisibility({ viewMode, pager }) {
		const visible = viewMode === 'list' && pager?.hasMore;
		if (this.els.pager) {
			this.els.pager.toggleAttribute('hidden', !visible);
		}
	}

	setPagerStatus({ loaded = 0, total = 0 } = {}) {
		if (this.els.loadedCount) this.els.loadedCount.textContent = String(loaded);
		if (this.els.totalCount) this.els.totalCount.textContent = String(total);
		if (this.els.resultsCount) this.els.resultsCount.textContent = String(total); // "X resultados" (opcional)
	}

	setInputs({ query, regionId, subjectId } = {}) {
		if (this.els.searchInput && typeof query === 'string') this.els.searchInput.value = query;
		if (this.els.subjectSelect) this.els.subjectSelect.value = subjectId || '';
		if (this.els.regionSelect) this.els.regionSelect.value = regionId || '';
	}

	/* =========================
	   Internals
	========================= */

	_emit(name, payload) {
		const fn = this._handlers[name];
		if (typeof fn === 'function') fn(payload || {});
	}

	_getTemplate(id) {
		const tpl = document.querySelector(id);
		// Permite arrancar sin templates, pero avisamos
		if (!tpl) return null;
		return tpl;
	}

	_clone(tpl) {
		if (!tpl) return document.createElement('div');
		const fragment = tpl.content.cloneNode(true);
		// devolvemos primer elemento “útil”
		return fragment.firstElementChild || fragment;
	}

	_toggleActive(el, isActive) {
		if (!el) return;
		el.classList.toggle('is-active', Boolean(isActive));
	}

	_trimToAlphanumeric(text = '', max = 12, minAlnumTail = 3) {
		const str = String(text).trim();

		if (str.length <= max) return str;

		// Cortamos un poco más largo para poder ajustar
		let cut = str.slice(0, max + minAlnumTail);

		// Buscamos el último punto donde haya al menos minAlnumTail alfanuméricos seguidos
		const regex = new RegExp(`([a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ]{${minAlnumTail},})[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ]*$`, 'u');

		const match = cut.match(regex);

		if (match) {
			cut = cut.slice(0, cut.lastIndexOf(match[1]) + match[1].length);
		} else {
			// fallback seguro
			cut = cut.slice(0, max).replace(/\W+$/u, '');
		}

		return cut + '…';
	}
}
